/* global google */
import React from 'react';
import { Circle, GoogleMap } from '@react-google-maps/api';
import { Typography } from '@mui/material';

import { MAX_CIRCLE_RADIUS_M, MIN_CIRCLE_RADIUS_M } from '@utils/tenantRoutes';
import RoutePolygons from './RoutePolygons';
import type { ColoredRoute } from './RoutePolygons';
import { COLOR_TEAL, COLOR_TEAL_SURFACE } from '../../../theme';

type CenterType = { lat: number, lng: number };

type PropsType = {
    height?: number | string,
    /** Other company routes, shown behind the circle (not editable or clickable) */
    otherRoutes?: ColoredRoute[],
    center: CenterType | null,
    radiusM: number,
    onChange?: (center: CenterType, radiusM: number) => void,
    /** Display only: the circle can't be moved or resized */
    readOnly?: boolean
};

const defaultCenter = { lat: 63, lng: 16 };
const defaultZoom = 5;
const mapOptions = {
    mapTypeControl: false,
    fullscreenControl: false,
    streetViewControl: false,
    clickableIcons: false,
    minZoom: 4,
    maxZoom: 20
};

const circleOptions = {
    strokeColor: COLOR_TEAL,
    strokeOpacity: 1,
    strokeWeight: 2.5,
    fillColor: COLOR_TEAL_SURFACE,
    fillOpacity: 0.4,
    clickable: true,
    draggable: true,
    editable: true,
    zIndex: 2
};

const readOnlyCircleOptions = {
    ...circleOptions, clickable: false, draggable: false, editable: false
};

/** Circle edited directly on the map (move by dragging, resize by the edge handle); radius clamped as in the API. */
export default function EditableCircleMap(props: PropsType) {
    const {
        height = 360,
        center,
        radiusM,
        onChange,
        otherRoutes = [],
        readOnly = false
    } = props;

    const mapRef = React.useRef<google.maps.Map | null>(null);
    const circleRef = React.useRef<google.maps.Circle | null>(null);
    const [mapReady, setMapReady] = React.useState(false);
    const draggingRef = React.useRef(false);
    // Latest props, so commit skips the change events fired by our own prop updates
    const propsRef = React.useRef({ center, radiusM });
    propsRef.current = { center, radiusM };

    const commit = React.useCallback(() => {
        const circle = circleRef.current;
        const c = circle?.getCenter();
        if (!circle || !c || draggingRef.current) return;
        const clamped = Math.min(MAX_CIRCLE_RADIUS_M, Math.max(MIN_CIRCLE_RADIUS_M, circle.getRadius()));
        if (clamped !== circle.getRadius()) {
            circle.setRadius(clamped);
        }
        const next = { lat: c.lat(), lng: c.lng() };
        const radius = Math.round(clamped);
        const prev = propsRef.current;
        if (
            prev.center
            && Math.abs(prev.center.lat - next.lat) < 1e-9
            && Math.abs(prev.center.lng - next.lng) < 1e-9
            && Math.abs(prev.radiusM - radius) < 1
        ) {
            return;
        }
        onChange?.(next, radius);
    }, [onChange]);

    // Zoom to the circle when it was changed from outside (place search, shape switch)
    React.useEffect(() => {
        const map = mapRef.current;
        if (!mapReady || !map || !center) return;
        const circle = circleRef.current;
        const current = circle?.getCenter();
        if (
            circle && current
            && Math.abs(current.lat() - center.lat) < 1e-9
            && Math.abs(current.lng() - center.lng) < 1e-9
            && Math.abs(circle.getRadius() - radiusM) < 1
            && map.getBounds()?.contains(current)
        ) {
            return;
        }
        const bounds = new window.google.maps.Circle({ center, radius: radiusM }).getBounds();
        if (bounds) {
            map.fitBounds(bounds);
        }
    }, [mapReady, center, radiusM]);

    return (
        <>
            <GoogleMap
                mapContainerStyle={{ height, width: '100%', overflow: 'hidden' }}
                center={defaultCenter}
                zoom={defaultZoom}
                options={mapOptions}
                onLoad={(map) => {
                    mapRef.current = map;
                    setMapReady(true);
                }}
                onUnmount={() => {
                    mapRef.current = null;
                    setMapReady(false);
                }}
            >
                <RoutePolygons routes={otherRoutes} />
                {center && (
                    <Circle
                        center={center}
                        radius={radiusM}
                        options={readOnly ? readOnlyCircleOptions : circleOptions}
                        onLoad={(circle) => {
                            circleRef.current = circle;
                        }}
                        onUnmount={() => {
                            circleRef.current = null;
                        }}
                        onDragStart={() => {
                            draggingRef.current = true;
                        }}
                        onDragEnd={() => {
                            draggingRef.current = false;
                            commit();
                        }}
                        onCenterChanged={commit}
                        onRadiusChanged={commit}
                    />
                )}
            </GoogleMap>
            {!readOnly && (
                <Typography variant="caption" component="p" sx={{ px: 2, py: 1.5 }}>
                    {center
                        ? `Radie ${Math.round(radiusM / 1000)} km. Dra var som helst i cirkeln för att flytta den, eller i handtaget i kanten för att ändra radien.`
                        : 'Sök efter en plats för att placera cirkeln.'}
                </Typography>
            )}
        </>
    );
}
